// Shared hazard-watch logic for web (safety-react) and RN (safety-native).
// Single source of truth — the two apps previously carried divergent copies
// of this code, which caused repeated storm-visibility bugs.

// ── Types ─────────────────────────────────────────────────────────────────────

export type HazardAlertLevel = 'green' | 'orange' | 'red'

export interface HazardEvent {
  id: string
  source: 'gdacs' | 'usgs' | 'tomorrow'
  title: string
  category: string
  alertLevel: HazardAlertLevel | null
  severity: string | null
  date: string
  point: [number, number] | null   // [lat, lng] — current/latest position
  track: Array<[number, number]> | null  // [lat, lng][] — full TC track when available
  country: string | null
  sourceUrl: string | null
}

/** Maps a Cloud Function proxy name (e.g. 'gdacsProxy') to a full URL. */
export type HazardProxyBase = (fn: string) => string

// ── Geographic constants ──────────────────────────────────────────────────────

/** PH bbox for the supplemental low-magnitude USGS fetch: minLon,maxLat,maxLon,minLat */
export const PH_BBOX = '116.0000,21.0000,128.0000,5.0000'

/** Official PAGASA PAR box. */
export const PAR_BOUNDS = { minLat: 5, maxLat: 25, minLng: 115, maxLng: 135 }

/**
 * Storm watch zone: PAR + 4.5° buffer on all sides, extended east to 150°E.
 * WPac typhoons form near Guam (~145°E) and track west into PAR, so the
 * eastern edge must reach well past the PAR boundary or approaching storms
 * (e.g. Bavi near Guam) are invisible until they're already close.
 */
export const STORM_WATCH_BOUNDS = { minLat: 0.5, maxLat: 29.5, minLng: 110.5, maxLng: 150 }

export function inPAR([lat, lng]: [number, number]): boolean {
  return lat >= PAR_BOUNDS.minLat && lat <= PAR_BOUNDS.maxLat &&
         lng >= PAR_BOUNDS.minLng && lng <= PAR_BOUNDS.maxLng
}

export function inStormWatchZone([lat, lng]: [number, number]): boolean {
  return lat >= STORM_WATCH_BOUNDS.minLat && lat <= STORM_WATCH_BOUNDS.maxLat &&
         lng >= STORM_WATCH_BOUNDS.minLng && lng <= STORM_WATCH_BOUNDS.maxLng
}

/**
 * A storm matters to PH users if its current position OR any point of its
 * track falls inside the watch zone. Track-aware so a storm that formed in
 * the zone but whose latest fix is momentarily outside still shows.
 */
export function isParRelevantStorm(e: HazardEvent): boolean {
  if (e.category !== 'severeStorms') return false
  if (e.point && inStormWatchZone(e.point)) return true
  return e.track?.some(p => inStormWatchZone(p)) ?? false
}

// ── Geo utilities ─────────────────────────────────────────────────────────────

export function distanceKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
}

/** Builds a bbox string (minLon,maxLat,maxLon,minLat) around a point. */
export function buildHazardBbox(lat: number, lng: number, radiusKm: number): string {
  const degLat = radiusKm / 111
  const degLng = radiusKm / (111 * Math.cos((lat * Math.PI) / 180))
  return [
    (lng - degLng).toFixed(4),
    (lat + degLat).toFixed(4),
    (lng + degLng).toFixed(4),
    (lat - degLat).toFixed(4),
  ].join(',')
}

// ── Normalization helpers ─────────────────────────────────────────────────────

export function normalizeHazardAlertLevel(level: string): HazardAlertLevel | null {
  const l = level.toLowerCase()
  if (l === 'red') return 'red'
  if (l === 'orange') return 'orange'
  if (l === 'green') return 'green'
  return null
}

function gdacsTypeToCategory(type: string): string {
  switch (type) {
    case 'TC': return 'severeStorms'
    case 'EQ': return 'earthquakes'
    case 'FL': return 'floods'
    case 'VO': return 'volcanoes'
    case 'TS': return 'tsunami'
    default:   return 'manmade'
  }
}

function tomorrowEventToCategory(event: string): string {
  const e = event.toLowerCase()
  if (/typhoon|tropical|cyclone|hurricane|tornado|storm|wind|thunder/i.test(e)) return 'severeStorms'
  if (/flood|rain|precipitation/i.test(e)) return 'floods'
  if (/fire|wildfire/i.test(e)) return 'wildfires'
  if (/earthquake|seismic/i.test(e)) return 'earthquakes'
  if (/volcano|eruption/i.test(e)) return 'volcanoes'
  if (/tsunami/i.test(e)) return 'tsunami'
  if (/snow|blizzard|ice/i.test(e)) return 'snow'
  if (/heat|cold|freeze|frost|temperature/i.test(e)) return 'tempExtremes'
  if (/dust|fog|haze|smoke/i.test(e)) return 'dustHaze'
  return 'manmade'
}

function normalizeStormName(name: string): string {
  return name
    .toLowerCase()
    .replace(/^(super typhoon|typhoon|tropical storm|tropical depression|cyclone|hurricane|ts |td |ty )\s*/i, '')
    .trim()
}

/** Merge the same storm reported by multiple sources; prefer GDACS data. */
export function dedupeStormEvents(events: HazardEvent[]): HazardEvent[] {
  const storms = events.filter(e => e.category === 'severeStorms')
  const others = events.filter(e => e.category !== 'severeStorms')
  const groups = new Map<string, HazardEvent[]>()
  for (const e of storms) {
    const key = normalizeStormName(e.title)
    if (!groups.has(key)) groups.set(key, [])
    groups.get(key)!.push(e)
  }
  const merged: HazardEvent[] = []
  for (const group of groups.values()) {
    if (group.length === 1) { merged.push(group[0]); continue }
    const primary = group.find(e => e.source === 'gdacs') ?? group[0]
    const fallback = group.find(e => e !== primary && e.point)
    merged.push({ ...primary, point: primary.point ?? fallback?.point ?? null })
  }
  return [...merged, ...others]
}

// ── GDACS ─────────────────────────────────────────────────────────────────────

interface GdacsFeature {
  type: string
  geometry: { type: string; coordinates: unknown } | null
  properties: {
    eventtype: string
    eventid: number
    name: string
    country: string | null
    alertlevel: string
    fromdate: string
    todate?: string
    iscurrent?: boolean | string
    severitydata?: { severitytext: string }
    bbox?: string
  }
}

/**
 * GDACS `todate` is the end of the current advisory window, NOT the event
 * end — active storms routinely carry a todate hours or days in the past
 * (verified live 2026-07-05: Maysak iscurrent=true, todate=yesterday).
 * `iscurrent` is the authoritative flag; todate only backstops when the
 * flag is missing, with a 72h grace period.
 */
function isGdacsEventActive(p: GdacsFeature['properties']): boolean {
  if (p.iscurrent !== undefined) {
    return p.iscurrent === true || p.iscurrent === 'true'
  }
  if (!p.todate) return true
  return new Date(p.todate).getTime() >= Date.now() - 72 * 3600_000
}

function parseGdacsGeometry(geom: GdacsFeature['geometry'], bboxStr?: string): {
  point: [number, number] | null
  track: Array<[number, number]> | null
} {
  let point: [number, number] | null = null
  let track: Array<[number, number]> | null = null
  if (geom?.type === 'Point') {
    const c = geom.coordinates as [number, number]
    point = [c[1], c[0]]
  } else if (geom?.type === 'LineString') {
    // TC track: last coordinate = most recent storm position
    const coords = geom.coordinates as Array<[number, number]>
    track = coords.map(c => [c[1], c[0]] as [number, number])
    const last = track[track.length - 1]
    if (last) point = last
  } else if (geom?.type === 'Polygon') {
    const ring = (geom.coordinates as Array<Array<[number, number]>>)[0] ?? []
    const avgLng = ring.reduce((s, c) => s + c[0], 0) / (ring.length || 1)
    const avgLat = ring.reduce((s, c) => s + c[1], 0) / (ring.length || 1)
    point = [avgLat, avgLng]
  }
  if (!point && bboxStr) {
    const parts = bboxStr.split(',').map(Number)
    if (parts.length === 4 && parts.every(n => !isNaN(n))) {
      const [minLon, minLat, maxLon, maxLat] = parts
      point = [(minLat + maxLat) / 2, (minLon + maxLon) / 2]
    }
  }
  return { point, track }
}

/**
 * Always fetches GLOBALLY — passing a bbox to the GDACS search endpoint makes
 * it hang until timeout (verified live 2026-07-05: bbox query never responds,
 * bbox-less query returns in <1s). The global 30-day list is small (~200 cap)
 * and callers filter by radius/watch-zone client-side anyway.
 */
export async function fetchGdacsEvents(
  opts: { proxyBase: HazardProxyBase; days: number },
  signal: AbortSignal,
): Promise<HazardEvent[]> {
  const toDate = new Date()
  const fromDate = new Date(toDate)
  fromDate.setDate(fromDate.getDate() - opts.days)
  const fmt = (d: Date) => d.toISOString().slice(0, 10)
  const params = new URLSearchParams({
    eventlist: 'TC,EQ,FL,VO,TS',
    alertlevel: 'Green,Orange,Red',
    fromDate: fmt(fromDate),
    toDate: fmt(toDate),
    limit: '200',
  })
  const res = await fetch(`${opts.proxyBase('gdacsProxy')}?${params}`, { signal })
  if (!res.ok) throw new Error(`GDACS ${res.status}`)
  const data = (await res.json()) as { features?: GdacsFeature[] }
  return (data.features ?? [])
    .filter(f => isGdacsEventActive(f.properties))
    .map(f => {
      const p = f.properties
      const { point, track } = parseGdacsGeometry(f.geometry, p.bbox)
      return {
        id: `gdacs_${p.eventid}`,
        source: 'gdacs' as const,
        title: p.name,
        category: gdacsTypeToCategory(p.eventtype),
        alertLevel: normalizeHazardAlertLevel(p.alertlevel),
        severity: p.severitydata?.severitytext ?? null,
        date: p.fromdate,
        point,
        track,
        country: p.country ?? null,
        sourceUrl: `https://www.gdacs.org/report.aspx?eventtype=${p.eventtype}&eventid=${p.eventid}`,
      }
    })
}

// ── USGS ──────────────────────────────────────────────────────────────────────

interface UsgsFeature {
  id: string
  geometry: { type: string; coordinates: [number, number, number] } | null
  properties: {
    mag: number
    place: string
    time: number
    alert: string | null
    url: string
    tsunami: number
    title: string
  }
}

export async function fetchUsgsEvents(
  opts: { proxyBase: HazardProxyBase; bbox?: string | null; days: number; minmag?: string },
  signal: AbortSignal,
): Promise<HazardEvent[]> {
  const endTime = new Date().toISOString().slice(0, 10)
  const startDate = new Date()
  startDate.setDate(startDate.getDate() - opts.days)
  const params = new URLSearchParams({
    format: 'geojson',
    minmagnitude: opts.minmag ?? '5.5',
    starttime: startDate.toISOString().slice(0, 10),
    endtime: endTime,
    orderby: 'time',
    limit: '100',
  })
  if (opts.bbox) {
    const [minLon, maxLat, maxLon, minLat] = opts.bbox.split(',').map(Number)
    params.set('minlatitude',  String(minLat))
    params.set('maxlatitude',  String(maxLat))
    params.set('minlongitude', String(minLon))
    params.set('maxlongitude', String(maxLon))
  }
  const res = await fetch(`${opts.proxyBase('usgsProxy')}?${params}`, { signal })
  if (!res.ok) throw new Error(`USGS ${res.status}`)
  const data = (await res.json()) as { features?: UsgsFeature[] }
  return (data.features ?? []).map(f => {
    const coords = f.geometry?.coordinates ?? null
    const p = f.properties
    return {
      id: `usgs_${f.id}`,
      source: 'usgs' as const,
      title: p.title,
      category: p.tsunami === 1 ? 'tsunami' : 'earthquakes',
      alertLevel: normalizeHazardAlertLevel(p.alert ?? ''),
      severity: `M${p.mag.toFixed(1)}`,
      date: new Date(p.time).toISOString(),
      point: coords ? [coords[1], coords[0]] as [number, number] : null,
      track: null,
      country: p.place ?? null,
      sourceUrl: p.url,
    }
  })
}

// ── Tomorrow.io ───────────────────────────────────────────────────────────────

interface TomorrowAlert {
  id?: string
  headline?: string
  title?: string
  event?: string
  severity?: string  // ADVISORY | WATCH | WARNING | EXTREME
  onset?: string
  description?: string
  senderName?: string
  country?: string
  locations?: Array<{ type: string; coordinates: number[] }>
}

export async function fetchTomorrowEvents(
  opts: { proxyBase: HazardProxyBase; lat: number; lng: number; country?: string | null },
  signal: AbortSignal,
): Promise<HazardEvent[]> {
  const res = await fetch(`${opts.proxyBase('tomorrowProxy')}?lat=${opts.lat}&lng=${opts.lng}`, { signal })
  if (!res.ok) return []
  const data = (await res.json()) as { data?: { alerts?: TomorrowAlert[] }; alerts?: TomorrowAlert[] }
  const alerts = data.data?.alerts ?? data.alerts ?? []
  return alerts.map((a, i) => {
    const sev = (a.severity ?? '').toUpperCase()
    let alertLevel: HazardAlertLevel = 'green'
    if (sev === 'EXTREME' || sev === 'WARNING') alertLevel = 'red'
    else if (sev === 'WATCH') alertLevel = 'orange'
    const coords = a.locations?.[0]?.coordinates
    const title = a.headline ?? a.title ?? a.event ?? 'Weather Alert'
    return {
      id: `tomorrow_${a.id ?? `${opts.lat}_${opts.lng}_${i}`}`,
      source: 'tomorrow' as const,
      title,
      category: tomorrowEventToCategory(a.event ?? title),
      alertLevel,
      severity: a.description?.split('\n')[0]?.slice(0, 120) ?? null,
      date: a.onset ?? new Date().toISOString(),
      point: coords && coords.length >= 2
        ? [coords[1], coords[0]] as [number, number]
        : [opts.lat, opts.lng] as [number, number],
      track: null,
      country: opts.country ?? a.country ?? a.senderName ?? null,
      sourceUrl: null,
    }
  })
}

// ── Orchestrator ──────────────────────────────────────────────────────────────

export interface LoadHazardsResult {
  events: HazardEvent[]
  /** Sources that failed or timed out: 'gdacs' | 'usgs' | 'usgsPH' | 'tomorrow' */
  failures: string[]
}

function withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
  return Promise.race([
    p,
    new Promise<never>((_, reject) => setTimeout(() => reject(new Error('timeout')), ms)),
  ])
}

/**
 * Fetches all hazard sources in parallel (10s timeout each) and returns a
 * deduplicated event list. GDACS is fetched globally (bbox hangs the API and
 * the list is small), so PH-relevant typhoons are always present regardless
 * of the user's location/radius — callers scope client-side.
 */
export async function loadHazardEvents(
  opts: {
    proxyBase: HazardProxyBase
    lat: number | null
    lng: number | null
    radiusKm: number
    days?: number
    country?: string | null
  },
  signal: AbortSignal,
): Promise<LoadHazardsResult> {
  const days = opts.days ?? 30
  const { proxyBase, lat, lng } = opts
  const bbox = lat !== null && lng !== null ? buildHazardBbox(lat, lng, opts.radiusKm) : null

  const [gdacs, usgs, usgsPH, tomorrow] = await Promise.allSettled([
    withTimeout(fetchGdacsEvents({ proxyBase, days }, signal), 10_000),
    withTimeout(fetchUsgsEvents({ proxyBase, bbox, days, minmag: '5.5' }, signal), 10_000),
    withTimeout(fetchUsgsEvents({ proxyBase, bbox: PH_BBOX, days, minmag: '5.0' }, signal), 10_000),
    lat !== null && lng !== null
      ? withTimeout(fetchTomorrowEvents({ proxyBase, lat, lng, country: opts.country }, signal), 10_000)
      : Promise.resolve([] as HazardEvent[]),
  ])

  const failures: string[] = []
  const pick = (r: PromiseSettledResult<HazardEvent[]>, name: string): HazardEvent[] => {
    if (r.status === 'fulfilled') return r.value
    failures.push(name)
    return []
  }

  const all: HazardEvent[] = [
    ...pick(gdacs, 'gdacs'),
    ...pick(usgs, 'usgs'),
    ...pick(usgsPH, 'usgsPH'),
    ...pick(tomorrow, 'tomorrow'),
  ]

  const seen = new Set<string>()
  const unique = all.filter(e => { if (seen.has(e.id)) return false; seen.add(e.id); return true })
  return { events: dedupeStormEvents(unique), failures }
}
